import { useDispatch } from 'react-redux';

const httpEndpoint = `${process.env.GATSBY_CC_GQL_URL}/graphql`;

export const getSession = async (
    {
        dispatch,
        updateSavedSession,
        updateZipAndCounty,
    }: {
        dispatch: ReturnType<typeof useDispatch>;
        updateSavedSession: (data: ISavedSession) => {
            type: string;
            payload: ISavedSession;
        };
        updateZipAndCounty: (
            zip: string,
            countyName: string
        ) => {
            type: string;
            payload: { zip: string; countyName: string };
        };
    },
    {
        sessionCode,
        allid,
        planYear,
    }: {
        sessionCode?: string;
        allid: string;
        planYear: number;
    }
) => {
    if (!sessionCode) {
        throw 'sessionCode required';
    }
    try {
        await fetch(httpEndpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
                query: `
                        query GetSavedSession($code1: String, $planYear: Int!, $allid: String!) {
                            savedSession(code1: $code1, planYear: $planYear, allid: $allid, ) {
                                code1
                                census {
                                    zip
                                    countyName
                                }
                                medications {
                                    ehiDrugId
                                }
                                providers {
                                    providerId
                                }
                            }
                        }
                    `,
                variables: {
                    code1: sessionCode,
                    planYear: planYear,
                    allid: allid,
                },
            }),
        }).then((response) => {
            response
                .json()
                .then(
                    (res: {
                        data?: {
                            savedSession: {
                                code1: string;
                                medications: any[];
                                providers: any[];
                                census: { zip: string; countyName: string };
                            };
                        };
                        errors?: any;
                    }) => {
                        if (!res.data) {
                            throw res.errors;
                        }
                        if (res.data.savedSession) {
                            const {
                                code1: resCode,
                                medications,
                                providers,
                            } = res.data.savedSession;

                            dispatch(
                                updateSavedSession({
                                    sessionCode: resCode,
                                    medications: medications,
                                    providers: providers,
                                })
                            );
                            if (res.data.savedSession.census?.zip) {
                                const { zip, countyName } = res.data.savedSession.census;
                                dispatch(updateZipAndCounty(zip, countyName));
                            }
                        }
                    }
                )
                .catch((e) => {
                    throw e;
                });
        });
    } catch (e) {
        console.error(e);
    }
};

export const patchSession = async (
    {
        dispatch,
        updateGeoLocation,
    }: {
        dispatch: ReturnType<typeof useDispatch>;
        updateGeoLocation: (geoData: { zip: string; countyName: string }) => {
            type: string;
            payload: { zip: string; countyName: string };
        };
    },
    {
        allid,
        sessionCode,
        planYear,
        census,
    }: { allid: string; sessionCode: string; planYear: number; census: Record<string, string> }
) => {
    try {
        await fetch(httpEndpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
                query: `
                    mutation patchSavedSession(
                        $allid: String!
                        $code1: String!
                        $planYear: Int
                        $census: CensusInfoInput
                    ) {
                        patchSavedSession(
                            input: {
                                allid: $allid
                                code1: $code1
                                planYear: $planYear
                                census: $census
                            }
                        ) {
                            code1
                            success
                        }
                    }
                `,
                variables: {
                    allid: allid,
                    code1: sessionCode,
                    planYear: planYear,
                    census: census,
                },
            }),
        }).then((response) => {
            response
                .json()
                .then((res: { data: { success: boolean } }) => {
                    if (res.data?.success) {
                        const { zip, countyName } = census;
                        dispatch(updateGeoLocation({ zip, countyName }));
                    }
                })
                .catch((e) => {
                    throw e;
                });
        });
    } catch (e) {
        console.error(e);
    }
};

export const getLoggedInUser = async (
    sessionCode: string,
    {
        dispatch,
        updateUser,
        updateFavorites,
    }: {
        dispatch: ReturnType<typeof useDispatch>;
        updateUser: (data: any) => { type: string; payload: any };
        updateFavorites: (data: number) => { type: string; payload: number };
    }
) => {
    return new Promise((resolve, reject) => {
        try {
            fetch(httpEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    query: `
                    query getUserProfile($profileId: String, $savedSessionCode: String) {
                        customerProfile(profileId: $profileId, savedSessionCode: $savedSessionCode) {
                          plans {
                            favoritePlans {
                              ehiPlanId
                            }
                          }
                        }
                        isUserLoggedIn {
                          isLoggedIn
                          user {
                            firstName
                            lastName
                            loginId
                            uuid
                            leadId
                            savedSessionCode
                            profileId
                            isAccountActivated
                          }
                        }
                    }
            `,
                    variables: {
                        savedSessionCode: sessionCode,
                    },
                }),
            })
                .then((response) => {
                    response
                        .json()
                        .then(
                            (res: {
                                data: {
                                    isUserLoggedIn: {
                                        isLoggedIn: boolean;
                                        user: { isAccountActivated: boolean };
                                    };
                                    customerProfile: {
                                        plans: {
                                            favoritePlans: Array<string>;
                                        };
                                    };
                                };
                            }) => {
                                const { isUserLoggedIn, customerProfile } = res.data;
                                const favoritesCount = customerProfile?.plans?.favoritePlans.length;
                                if (favoritesCount) {
                                    dispatch(updateFavorites(favoritesCount));
                                }
                                const isLoggedInAndActivated =
                                    isUserLoggedIn.isLoggedIn &&
                                    isUserLoggedIn.user &&
                                    isUserLoggedIn.user.isAccountActivated;
                                if (isLoggedInAndActivated) {
                                    dispatch(
                                        updateUser({
                                            isLoggedIn: isUserLoggedIn.isLoggedIn,
                                            data: isUserLoggedIn.user,
                                        })
                                    );
                                    resolve({ error: null });
                                } else {
                                    resolve({ error: 'not logged in' });
                                }
                            }
                        )
                        .catch((e) => {
                            throw e;
                        });
                })
                .catch((e) => {
                    throw e;
                });
        } catch (e) {
            reject({ error: e });
        }
    });
};

type FavoritesResponse = {
    customerProfile: {
        plans: {
            favoritePlans: Array<string>;
        };
    };
};

export const getUserFavorites = async (sessionCode: string): Promise<FavoritesResponse> => {
    return new Promise((resolve, reject) => {
        try {
            fetch(httpEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    query: `
                    query getUserProfile($profileId: String, $savedSessionCode: String) {
                        customerProfile(profileId: $profileId, savedSessionCode: $savedSessionCode) {
                          plans {
                            favoritePlans {
                              ehiPlanId
                            }
                          }
                        }
                    }
            `,
                    variables: {
                        savedSessionCode: sessionCode,
                    },
                }),
            })
                .then((response) => {
                    response
                        .json()
                        .then((res: { data: FavoritesResponse }) => {
                            resolve(res.data);
                        })
                        .catch((e) => {
                            throw e;
                        });
                })
                .catch((e) => {
                    throw e;
                });
        } catch (e) {
            reject({ error: e });
        }
    });
};
